import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { useParams } from "react-router-dom";
import { Plus, X, Edit, Trash2, Loader2 } from "lucide-react";

const InterviewPrepDetail = () => {
  const { id } = useParams();
  const [prep, setPrep] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    question: "",
    type: "objective",
    options: ["", "", "", ""],
    correctAnswer: "",
    difficulty: "beginner",
  });

  // Fetch prep detail
  const fetchPrepDetail = async () => {
    const docRef = doc(db, "interviewPreps", id);
    const snap = await getDoc(docRef);
    if (snap.exists()) setPrep({ id: snap.id, ...snap.data() });

    const qSnap = await getDocs(
      collection(db, "interviewPreps", id, "questions")
    );
    setQuestions(qSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchPrepDetail();
  }, []);

  // Add or Edit Question
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.question.trim()) return alert("Please enter a question!");
    setLoading(true);

    try {
      if (editId) {
        await updateDoc(doc(db, "interviewPreps", id, "questions", editId), {
          ...form,
          updatedAt: serverTimestamp(),
        });
        alert("✅ Question updated!");
      } else {
        await addDoc(collection(db, "interviewPreps", id, "questions"), {
          ...form,
          createdAt: serverTimestamp(),
        });
        alert("✅ Question added!");
      }

      setShowModal(false);
      setEditId(null);
      resetForm();
      fetchPrepDetail();
    } catch (err) {
      console.error(err);
      alert("❌ Error saving question");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      question: "",
      type: "objective",
      options: ["", "", "", ""],
      correctAnswer: "",
      difficulty: "beginner",
    });
  };

  const handleEdit = (q) => {
    setEditId(q.id);
    setForm({
      question: q.question,
      type: q.type,
      options: q.options || ["", "", "", ""],
      correctAnswer: q.correctAnswer || "",
      difficulty: q.difficulty || "beginner",
    });
    setShowModal(true);
  };

  const confirmDelete = (qid) => {
    setDeleteId(qid);
    setShowConfirm(true);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteDoc(doc(db, "interviewPreps", id, "questions", deleteId));
      alert("🗑️ Question deleted!");
      setShowConfirm(false);
      fetchPrepDetail();
    } catch (err) {
      console.error(err);
      alert("❌ Error deleting question");
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  };

  return (
    <Wrapper>
      {prep ? (
        <>
          <Header>
            <div>
              <Title>{prep.title}</Title>
              <Description>
                {prep.description || "No description provided."}
              </Description>
              <Meta>
                Created on:{" "}
                {prep.createdAt?.toDate
                  ? prep.createdAt.toDate().toLocaleDateString()
                  : "N/A"}
              </Meta>
            </div>
            <AddButton
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
            >
              <Plus size={18} /> Add Question
            </AddButton>
          </Header>

          <QuestionList>
            {questions.length ? (
              questions.map((q, i) => (
                <QuestionCard key={q.id}>
                  <TopRow>
                    <h3>
                      {i + 1}. {q.question}
                    </h3>
                    <Actions>
                      <EditBtn onClick={() => handleEdit(q)}>
                        <Edit size={16} />
                      </EditBtn>
                      <DeleteBtn onClick={() => confirmDelete(q.id)}>
                        <Trash2 size={16} />
                      </DeleteBtn>
                    </Actions>
                  </TopRow>

                  <TagRow>
                    <TypeTag type={q.type}>{q.type}</TypeTag>
                    <DifficultyTag level={q.difficulty}>
                      {q.difficulty}
                    </DifficultyTag>
                  </TagRow>

                  {q.type === "objective" && (
                    <ul>
                      {q.options.map((opt, idx) => (
                        <li
                          key={idx}
                          style={{
                            color: opt === q.correctAnswer ? "#c59fff" : "#ccc",
                            fontWeight: opt === q.correctAnswer ? "600" : "400",
                          }}
                        >
                          {opt}
                        </li>
                      ))}
                    </ul>
                  )}
                </QuestionCard>
              ))
            ) : (
              <Empty>No questions added yet.</Empty>
            )}
          </QuestionList>

          {/* ===== Add/Edit Modal ===== */}
          {showModal && (
            <ModalOverlay>
              <ModalContent>
                <CloseIcon
                  onClick={() => {
                    setShowModal(false);
                    setEditId(null);
                  }}
                >
                  <X size={20} />
                </CloseIcon>
                <h2>{editId ? "Edit Question" : "Add Question"}</h2>

                <Form onSubmit={handleSubmit}>
                  <Label>Question</Label>
                  <Input
                    value={form.question}
                    onChange={(e) =>
                      setForm({ ...form, question: e.target.value })
                    }
                    placeholder="Enter question text..."
                  />

                  <Label>Type</Label>
                  <Select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    <option value="objective">Objective</option>
                    <option value="coding">Coding</option>
                  </Select>

                  <Label>Difficulty Level</Label>
                  <Select
                    value={form.difficulty}
                    onChange={(e) =>
                      setForm({ ...form, difficulty: e.target.value })
                    }
                  >
                    <option value="beginner">Beginner</option>
                    <option value="experienced">Experienced</option>
                    <option value="expert">Expert</option>
                  </Select>

                  {form.type === "objective" && (
                    <>
                      <Label>Options</Label>
                      {form.options.map((opt, i) => (
                        <Input
                          key={i}
                          placeholder={`Option ${i + 1}`}
                          value={opt}
                          onChange={(e) => {
                            const newOptions = [...form.options];
                            newOptions[i] = e.target.value;
                            setForm({ ...form, options: newOptions });
                          }}
                        />
                      ))}

                      <Label>Correct Answer</Label>
                      <Input
                        placeholder="Enter correct option"
                        value={form.correctAnswer}
                        onChange={(e) =>
                          setForm({ ...form, correctAnswer: e.target.value })
                        }
                      />
                    </>
                  )}

                  <SubmitButton type="submit" disabled={loading}>
                    {loading ? (
                      <LoaderIcon />
                    ) : editId ? (
                      "Update Question"
                    ) : (
                      "Save Question"
                    )}
                  </SubmitButton>
                </Form>
              </ModalContent>
            </ModalOverlay>
          )}

          {/* ===== Confirm Delete Modal ===== */}
          {showConfirm && (
            <ModalOverlay>
              <ModalContent>
                <h3>Are you sure you want to delete this question?</h3>
                <ConfirmActions>
                  <CancelButton onClick={() => setShowConfirm(false)}>
                    Cancel
                  </CancelButton>
                  <DeleteConfirmButton
                    onClick={handleDelete}
                    disabled={loading}
                  >
                    {loading ? <LoaderIcon /> : "Yes, Delete"}
                  </DeleteConfirmButton>
                </ConfirmActions>
              </ModalContent>
            </ModalOverlay>
          )}
        </>
      ) : (
        <Loading>Loading...</Loading>
      )}
    </Wrapper>
  );
};

export default InterviewPrepDetail;

//
// ==================== STYLED COMPONENTS ====================
//
const Wrapper = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
  padding: 40px;
  color: #fff;
  font-family: "Inter", sans-serif;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoaderIcon = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
  color: #fff;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const CancelButton = styled.button`
  background: transparent;
  color: #ccc;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
`;

const DeleteConfirmButton = styled.button`
  background: #ff5c5c;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: 0.3s;
  &:hover {
    background: #e03b3b;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 40px;
`;

const Title = styled.h2`
  font-size: 2rem;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Description = styled.p`
  color: #ccc;
  margin-top: 10px;
  max-width: 600px;
`;

const Meta = styled.span`
  display: block;
  color: #999;
  font-size: 0.9rem;
  margin-top: 8px;
`;

const AddButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 12px 18px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.5);
  }
`;

const QuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const QuestionCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 12px;
  padding: 20px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const EditBtn = styled.button`
  background: #9b5cff;
  border: none;
  color: #fff;
  border-radius: 8px;
  padding: 6px;
  cursor: pointer;
  &:hover {
    background: #c59fff;
  }
`;

const DeleteBtn = styled(EditBtn)`
  background: #ff5c5c;
  &:hover {
    background: #e03b3b;
  }
`;

const TagRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`;

const TypeTag = styled.span`
  background: ${({ type }) =>
    type === "objective"
      ? "rgba(155, 92, 255, 0.2)"
      : "rgba(255, 107, 107, 0.2)"};
  color: ${({ type }) => (type === "objective" ? "#c59fff" : "#ff6b6b")};
  border: 1px solid
    ${({ type }) => (type === "objective" ? "#9b5cff" : "#ff6b6b")};
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.8rem;
  text-transform: capitalize;
`;

const DifficultyTag = styled.span`
  background: ${({ level }) =>
    level === "beginner"
      ? "rgba(0, 200, 255, 0.2)"
      : level === "experienced"
      ? "rgba(255, 165, 0, 0.2)"
      : "rgba(255, 0, 128, 0.2)"};
  color: ${({ level }) =>
    level === "beginner"
      ? "#00d4ff"
      : level === "experienced"
      ? "#ffa500"
      : "#ff4da6"};
  border: 1px solid
    ${({ level }) =>
      level === "beginner"
        ? "#00d4ff"
        : level === "experienced"
        ? "#ffa500"
        : "#ff4da6"};
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.8rem;
  text-transform: capitalize;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(10, 0, 20, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99;
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 30px;
  width: 100%;
  max-width: 600px;
  backdrop-filter: blur(10px);
  position: relative;
  box-shadow: 0 0 20px rgba(155, 92, 255, 0.4);
  color: #fff;
`;

const CloseIcon = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  cursor: pointer;
  color: #c59fff;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 20px;
`;

const Label = styled.label`
  color: #c59fff;
  font-weight: 500;
`;

const Input = styled.input`
  background: rgba(20, 10, 35, 0.9);
  border: 1px solid rgba(155, 92, 255, 0.3);
  color: #fff;
  border-radius: 8px;
  padding: 10px 12px;
  outline: none;
  font-size: 1rem;
  &:focus {
    border-color: #c59fff;
  }
`;

const Select = styled.select`
  background: rgba(20, 10, 35, 0.9);
  border: 1px solid rgba(155, 92, 255, 0.3);
  color: #fff;
  border-radius: 8px;
  padding: 10px 12px;
  outline: none;
  font-size: 1rem;
  &:focus {
    border-color: #c59fff;
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.4);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Empty = styled.p`
  text-align: center;
  color: #aaa;
  margin-top: 40px;
`;

const Loading = styled.p`
  text-align: center;
  color: #c59fff;
  margin-top: 80px;
`;
